import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Cropper from "react-easy-crop";
import "./PostComposer.css";
import {
  selectUploadError,
  selectUploadStatus,
  selectPostsError,
  clearPostsError,
  selectPostsStatus,
  selectUploadSuccessMessage,
  selectSuccessMessage,
  clearUploadSuccessMessage,
  clearSuccessMessage,
} from "../../../redux/slices/postsSlice";

/* ========== Helpers for cropping ========== */
async function getCroppedDataURL(imageSrc, cropPixels, rotation = 0) {
  const image = await new Promise((res, rej) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = imageSrc;
  });

  const rads = (rotation * Math.PI) / 180;
  const safeW = Math.ceil(
    Math.abs(image.width * Math.cos(rads)) + Math.abs(image.height * Math.sin(rads))
  );
  const safeH = Math.ceil(
    Math.abs(image.width * Math.sin(rads)) + Math.abs(image.height * Math.cos(rads))
  );

  const canvas = document.createElement("canvas");
  canvas.width = safeW;
  canvas.height = safeH;
  const ctx = canvas.getContext("2d");

  ctx.translate(safeW / 2, safeH / 2);
  ctx.rotate(rads);
  ctx.drawImage(image, -image.width / 2, -image.height / 2);
  ctx.rotate(-rads);
  ctx.translate(-safeW / 2, -safeH / 2);

  const { x, y, width, height } = cropPixels;
  const data = ctx.getImageData(x, y, width, height);

  const out = document.createElement("canvas");
  out.width = width;
  out.height = height;
  const outCtx = out.getContext("2d");
  outCtx.putImageData(data, 0, 0);

  return out.toDataURL("image/jpeg", 0.92);
}

function dataURLtoFile(dataurl, filename = "cropped.jpg") {
  const [head, body] = dataurl.split(",");
  const mime = head.match(/:(.*?);/)?.[1] || "image/jpeg";
  const bin = atob(body);
  const len = bin.length;
  const u8 = new Uint8Array(len);
  for (let i = 0; i < len; i++) u8[i] = bin.charCodeAt(i);
  return new File([u8], filename, { type: mime });
}

const extractFrameFromVideoURL = (objectURL) =>
  new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.src = objectURL;
    video.muted = true;
    video.playsInline = true;

    const onLoaded = () => {
      const target = Math.min(0.1, Math.max(0.0, 0.1));
      const onSeeked = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = video.videoWidth || 320;
          canvas.height = video.videoHeight || 180;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          const dataURL = canvas.toDataURL("image/jpeg", 0.9);
          cleanup();
          resolve(dataURL);
        } catch (err) {
          cleanup();
          reject(err);
        }
      };
      video.currentTime = target;
      video.addEventListener("seeked", onSeeked, { once: true });
    };

    const cleanup = () => {
      video.removeAttribute("src");
      video.load();
    };

    video.addEventListener("loadeddata", onLoaded, { once: true });
    video.addEventListener("error", (e) => {
      cleanup();
      reject(e);
    });
  });

const PostComposer = ({
  onPublish,
  isPublishing = false,
  uploadFiles,
  isUploading = false,
  displayMessagesInParent = false,
  onError = null,
  onSuccess = null,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const uploadError = useSelector(selectUploadError);
  const createError = useSelector(selectPostsError);
  const uploadStatus = useSelector(selectUploadStatus);
  const createStatus = useSelector(selectPostsStatus);
  const uploadSuccessMessage = useSelector(selectUploadSuccessMessage);
  const createSuccessMessage = useSelector(selectSuccessMessage);

  const [displayError, setDisplayError] = useState(null);
  const [displaySuccess, setDisplaySuccess] = useState(null);
  const [isVisibleToDoctorsOnly, setIsVisibleToDoctorsOnly] = useState(false);
  const [postContent, setPostContent] = useState("");
  const [attachedFiles, setAttachedFiles] = useState([]);

  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const docInputRef = useRef(null);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedPixels, setCroppedPixels] = useState(null);
  const [aspect, setAspect] = useState(4 / 5);

  const handleToggleVisibility = () => setIsVisibleToDoctorsOnly((v) => !v);
  const handlePostContentChange = (e) => setPostContent(e.target.value);

  useEffect(() => {
    if (uploadError) {
      if (displayMessagesInParent && onError) {
        onError(uploadError);
      } else {
        setDisplayError(uploadError);
      }
      console.error("📤 Upload error:", uploadError);
    }
  }, [uploadError, displayMessagesInParent, onError]);

  useEffect(() => {
    if (createError) {
      if (displayMessagesInParent && onError) {
        onError(createError);
      } else {
        setDisplayError(createError);
      }
      console.error("📝 Post creation error:", createError);
    }
  }, [createError, displayMessagesInParent, onError]);

  useEffect(() => {
    if (uploadStatus === "succeeded" && uploadSuccessMessage) {
      if (displayMessagesInParent && onSuccess) {
        onSuccess(uploadSuccessMessage);
      } else {
        setDisplaySuccess(uploadSuccessMessage);
      }
    }
  }, [uploadStatus, uploadSuccessMessage, displayMessagesInParent, onSuccess]);

  useEffect(() => {
    if (createStatus === "succeeded" && createSuccessMessage) {
      if (displayMessagesInParent && onSuccess) {
        onSuccess(createSuccessMessage);
      } else {
        setDisplaySuccess(createSuccessMessage);
      }
    }
  }, [createStatus, createSuccessMessage, displayMessagesInParent, onSuccess]);

  useEffect(() => {
    if (!displaySuccess) return;

    const t = setTimeout(() => {
      setDisplaySuccess(null);
      dispatch(clearSuccessMessage());
      dispatch(clearUploadSuccessMessage());
    }, 3000);

    return () => clearTimeout(t);
  }, [displaySuccess, dispatch]);

  useEffect(() => {
    if (!displayError) return;

    const t = setTimeout(() => {
      setDisplayError(null);
      if (createError) dispatch(clearPostsError());
    }, 3000);

    return () => clearTimeout(t);
  }, [displayError, dispatch, createError]);

  const handlePublish = async () => {
    if (!postContent.trim() && attachedFiles.length === 0) {
      setDisplayError(t('postComposer.errors.emptyContent'));
      return;
    }

    setDisplayError(null);
    if (createError) dispatch(clearPostsError());

    try {
      let uploadedAttachments = [];

      if (attachedFiles.length > 0) {
        const filesToUpload = attachedFiles
          .map((att) => att.file)
          .filter((file) => file instanceof File);

        if (filesToUpload.length > 0) {
          const uploadResult = await uploadFiles(filesToUpload);

          if (uploadResult?.type?.endsWith('/fulfilled') && Array.isArray(uploadResult.payload)) {
            uploadedAttachments = uploadResult.payload;
          } else {
            throw new Error(typeof uploadResult?.payload === 'string' ? uploadResult.payload : uploadError || t('postComposer.errors.unknown'));
          }
        }
      }

      const postPayload = {
        title: postContent?.substring(0, 100) || t('postComposer.defaultTitle'),
        content: postContent.trim(),
        tags: [],
        visibleToDoctorsOnly: isVisibleToDoctorsOnly,
      };

      if (uploadedAttachments.length > 0) {
        postPayload.attachments = uploadedAttachments;

        const firstImage = uploadedAttachments.find((a) => a.type === "image");
        if (firstImage) postPayload.imageUrl = firstImage.url;
      }

      if (typeof onPublish === "function") {
        await onPublish(postPayload);

        setPostContent("");
        setAttachedFiles([]);
        setIsVisibleToDoctorsOnly(false);
        setDisplayError(null);
      }
    } catch (error) {
      const errorMessage = error?.message || error?.toString?.() || t('postComposer.errors.unknown');
      console.error("❌ Erreur lors de la publication:", error);
      setDisplayError(`❌ ${t('postComposer.errors.prefix')}: ${errorMessage}`);
    }
  };

  const handleImageUpload = () => imageInputRef.current?.click();
  const handleVideoUpload = () => videoInputRef.current?.click();
  const handleDocumentUpload = () => docInputRef.current?.click();

  const readFileAsDataURL = (file) =>
    new Promise((res, rej) => {
      const reader = new FileReader();
      reader.onload = () => res(reader.result);
      reader.onerror = rej;
      reader.readAsDataURL(file);
    });

  const handleFilesSelected = async (e, type) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const newItems = [];

    for (const file of files) {
      const id = Math.random().toString(36).substring(2, 11);
      try {
        if (type === "image") {
          const data = await readFileAsDataURL(file);
          newItems.push({ id, file, type: "image", name: file.name, size: file.size, preview: data });
        } else if (type === "video") {
          const fileURL = URL.createObjectURL(file);
          let thumb = null;
          try {
            thumb = await extractFrameFromVideoURL(fileURL);
          } catch (err) {
            console.warn("Video thumbnail extraction failed:", err);
          }
          newItems.push({ id, file, type: "video", name: file.name, size: file.size, url: fileURL, preview: thumb });
        } else if (type === "document") {
          const fileURL = URL.createObjectURL(file);
          newItems.push({ id, file, type: "document", name: file.name, size: file.size, url: fileURL, preview: fileURL });
        }
      } catch (err) {
        console.error("file read error", err);
      }
    }

    if (newItems.length) setAttachedFiles((prev) => [...prev, ...newItems]);
    e.target.value = "";
  };

  const handleRemoveFile = (fileId) => {
    setAttachedFiles((prev) => {
      const toRemove = prev.find((f) => f.id === fileId);
      if (toRemove?.url) URL.revokeObjectURL(toRemove.url);
      return prev.filter((f) => f.id !== fileId);
    });
  };

  useEffect(() => {
    return () => {
      attachedFiles.forEach((f) => f.url && URL.revokeObjectURL(f.url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openEditor = (index) => {
    const item = attachedFiles[index];
    if (!item) return;

    const editableSrc = item.type === "image" ? item.preview : item.type === "video" ? item.preview : null;
    if (!editableSrc) return;

    setEditingIndex(index);
    setEditorOpen(true);
    setZoom(1);
    setRotation(0);
    setCrop({ x: 0, y: 0 });
    setAspect(4 / 5);
  };

  const onCropComplete = (_, croppedAreaPixels) => setCroppedPixels(croppedAreaPixels);

  const applyCrop = async () => {
    if (editingIndex == null || !croppedPixels) {
      setEditorOpen(false);
      return;
    }
    try {
      const item = attachedFiles[editingIndex];
      if (!item) return;

      const srcToCrop = item.preview;
      if (!srcToCrop) return;

      const dataUrl = await getCroppedDataURL(srcToCrop, croppedPixels, rotation);

      setAttachedFiles((prev) =>
        prev.map((f, i) => {
          if (i !== editingIndex) return f;

          const updated = { ...f, preview: dataUrl };

          if (f.type === "image" && f.file?.type?.startsWith("image/")) {
            try {
              const newFile = dataURLtoFile(dataUrl, f.file.name || "cropped.jpg");
              updated.file = newFile;
            } catch (err) {
              console.warn("Failed to replace file with cropped version", err);
            }
          }
          return updated;
        })
      );
    } finally {
      setEditorOpen(false);
      setEditingIndex(null);
    }
  };

  const isDisabled = isPublishing || (!postContent.trim() && attachedFiles.length === 0);

  return (
    <div className="post-composer-container">
      {(isPublishing || isUploading) && (
        <div className="publishing-overlay">
          <div className="publishing-spinner"></div>
          <p>{isUploading ? t('postComposer.uploading') : t('postComposer.publishing')}</p>
        </div>
      )}

      {displayError && !displayMessagesInParent && (
        <div className="message-overlay error" role="alert">
          {displayError}
        </div>
      )}

      {displaySuccess && !displayMessagesInParent && (
        <div className="message-overlay success" role="alert">
          {displaySuccess}
        </div>
      )}

      <div className="post-composer-header">
        <div className="write-here-section">
          <span className="write-here-text">{t('postComposer.writeHere')}</span>
        </div>

        <div className="visibility-section">
          <span className="visibility-text">{t('postComposer.visibleToDoctors')}</span>
          <div className="post-composer-toggle-switch">
            <input
              type="checkbox"
              id="postComposerDoctorsToggle"
              checked={isVisibleToDoctorsOnly}
              onChange={handleToggleVisibility}
              disabled={isPublishing}
              aria-label={t('postComposer.visibleToDoctorsAriaLabel')}
            />
            <label htmlFor="postComposerDoctorsToggle" />
          </div>
        </div>
      </div>

      <div className="express-yourself-container">
        <textarea
          className="express-yourself-textarea"
          placeholder={t('postComposer.placeholder')}
          value={postContent}
          onChange={handlePostContentChange}
          disabled={isPublishing}
          aria-label={t('postComposer.textareaAriaLabel')}
        />
      </div>

      {attachedFiles.length > 0 && (
        <div className="preview-images-container">
          {attachedFiles.map((fileItem, index) => (
            <div key={fileItem.id} className="preview-image">
              {fileItem.type === "image" && fileItem.preview && (
                <img src={fileItem.preview} alt={fileItem.name} className="preview-img" />
              )}

              {fileItem.type === "video" && fileItem.url && (
                <video className="preview-video" controls src={fileItem.url}>
                  <source src={fileItem.url} type={fileItem.file?.type || "video/mp4"} />
                </video>
              )}

              {fileItem.type === "document" && fileItem.url && (
                <iframe src={fileItem.url} className="preview-document" title={fileItem.name} />
              )}

              <div className="preview-edit">
                {(fileItem.type === "image" || fileItem.type === "video") && fileItem.preview && (
                  <button
                    className="edit-photo-btn"
                    type="button"
                    onClick={() => openEditor(index)}
                    disabled={isPublishing}
                    aria-label={t('postComposer.editFile')}
                  >
                    ✎ {t('postComposer.edit')}
                  </button>
                )}

                <button
                  className="remove-photo-btn"
                  onClick={() => handleRemoveFile(fileItem.id)}
                  type="button"
                  disabled={isPublishing}
                  aria-label={t('postComposer.removeFile', { name: fileItem.name })}
                >
                  ✕
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editorOpen && editingIndex != null && (
        <div className="cropper-backdrop" role="dialog" aria-modal="true" aria-label={t('postComposer.cropper.title')}>
          <div className="cropper-modal">
            <div className="cropper-header">
              <strong>{t('postComposer.cropper.title')}</strong>
              <button 
                className="cropper-close" 
                onClick={() => setEditorOpen(false)} 
                type="button"
                aria-label={t('postComposer.cropper.close')}
              >
                ✕
              </button>
            </div>

            <div className="cropper-area">
              <Cropper
                image={attachedFiles[editingIndex]?.preview || ""}
                crop={crop}
                zoom={zoom}
                aspect={aspect}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onRotationChange={setRotation}
                onCropComplete={onCropComplete}
                restrictPosition={false}
              />
            </div>

            <div className="cropper-controls">
              <label className="ctrl">
                {t('postComposer.cropper.zoom')}
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.01}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  aria-label={t('postComposer.cropper.zoomControl')}
                />
              </label>

              <div className="aspect-group">
                <span>{t('postComposer.cropper.aspect')}</span>
                <button 
                  className={`asp ${aspect === 1 ? "active" : ""}`} 
                  onClick={() => setAspect(1)} 
                  type="button"
                  aria-label={t('postComposer.cropper.aspectRatio', { ratio: '1:1' })}
                >
                  1:1
                </button>
                <button
                  className={`asp ${aspect === 4 / 5 ? "active" : ""}`}
                  onClick={() => setAspect(4 / 5)}
                  type="button"
                  aria-label={t('postComposer.cropper.aspectRatio', { ratio: '4:5' })}
                >
                  4:5
                </button>
                <button
                  className={`asp ${aspect === 16 / 9 ? "active" : ""}`}
                  onClick={() => setAspect(16 / 9)}
                  type="button"
                  aria-label={t('postComposer.cropper.aspectRatio', { ratio: '16:9' })}
                >
                  16:9
                </button>
              </div>
            </div>

            <div className="cropper-actions">
              <button className="btn-secondary" onClick={() => setEditorOpen(false)} type="button">
                {t('postComposer.cropper.cancel')}
              </button>
              <button className="btn-primary" onClick={applyCrop} type="button">
                {t('postComposer.cropper.apply')}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="post-actions-section">
        <div className="media-buttons">
          <div 
            className={`media-button image-button ${isPublishing ? "disabled" : ""}`} 
            onClick={isPublishing ? null : handleImageUpload}
            role="button"
            tabIndex={isPublishing ? -1 : 0}
            aria-label={t('postComposer.addImage')}
          >
            <span className="media-text">{t('postComposer.image')}</span>
          </div>

          <div 
            className={`media-button video-button ${isPublishing ? "disabled" : ""}`} 
            onClick={isPublishing ? null : handleVideoUpload}
            role="button"
            tabIndex={isPublishing ? -1 : 0}
            aria-label={t('postComposer.addVideo')}
          >
            <span className="media-text">{t('postComposer.video')}</span>
          </div>

          <div 
            className={`media-button document-button ${isPublishing ? "disabled" : ""}`} 
            onClick={isPublishing ? null : handleDocumentUpload}
            role="button"
            tabIndex={isPublishing ? -1 : 0}
            aria-label={t('postComposer.addDocument')}
          >
            <span className="media-text">{t('postComposer.document')}</span>
          </div>

          <input
            type="file"
            ref={imageInputRef}
            accept="image/*"
            style={{ display: "none" }}
            multiple
            onChange={(e) => handleFilesSelected(e, "image")}
            disabled={isPublishing}
          />
          <input
            type="file"
            ref={videoInputRef}
            accept="video/*"
            style={{ display: "none" }}
            multiple
            onChange={(e) => handleFilesSelected(e, "video")}
            disabled={isPublishing}
          />
          <input
            type="file"
            ref={docInputRef}
            accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            style={{ display: "none" }}
            multiple
            onChange={(e) => handleFilesSelected(e, "document")}
            disabled={isPublishing}
          />
        </div>

        <button 
          className="publish-button" 
          onClick={handlePublish} 
          disabled={isDisabled}
          aria-label={t('postComposer.publishButton')}
        >
          <span className="publish-text">
            {isUploading ? t('postComposer.uploadingButton') : isPublishing ? t('postComposer.publishingButton') : t('postComposer.publish')}
          </span>
        </button>
      </div>
    </div>
  );
};

export default PostComposer;
